/**
 * @file Builds the schema inputs that confirm the investor makes their own
 * investment decisions and discloses past exempt-securities purchases.
 */
import type { RadioChangeEvent } from "antd";

import { Disclaimer, Markdown } from "@components/Text";
import { INVESTMENT_PROFILE_FIELD_MAP } from "@components/Domain";

import type { Form, Input, Organization, UpdateSchema } from "../types";

const LABEL_YES_REQUIRED = 'Required to be "Yes"';

const LABEL_LIABILITY = `
        I make my own investment decisions with respect to the investments in
        marketable securities, limited partnerships, limited liability
        companies or other private investments.
      `;

const LABEL_PURCHASED_EXEMPT_SECURITIES = `
  Have you ever purchased securities which are exempt from federal and
  state registration (private placements or limited offerings)?
`;

const TOOLTIP_PURCHASED_EXEMPT_SECURITIES = `
  If you have ever purchased an investment that is not publicly traded or have invested
  with us before (or in a similar real estate syndication) you may select, yes.
  Otherwise, you may select no.
`;

/**
 * Builds the liability-acceptance and exempt-securities inputs for the
 * given organization.
 */
const computeLiabilityInputs = (updateSchema: UpdateSchema, organization: Organization): Input[] => {
  const { email: organizationEmail } = organization;

  const messageAlert = `In order to participate in this investment opportunity
you must confirm that you make your own investment decisions. If you have any
questions, please email [${organizationEmail}](mailto:${organizationEmail}).`;

  /**
   * Clears the liability-acceptance error and refreshes the schema, adding
   * a warning message and blocking the schema when the investor declines.
   */
  const onChange = (event: RadioChangeEvent, form: Form) => {
    const { profileType } = form.getFieldsValue();
    const path = [ INVESTMENT_PROFILE_FIELD_MAP[profileType], "isLiabilityAccepted" ];

    form.setFields([
      {
        name: path,
        errors: [],
      },
    ]);

    const { value: isAccepted } = event.target;

    if (isAccepted) {
      return updateSchema();
    }

    // NOTE: shouldCustodianSign is a blocking field
    form.setFieldsValue({ profileSdira: { shouldCustodianSign: undefined } });
    updateSchema(<Markdown text={messageAlert} />, "isLiabilityAccepted");
  };

  return [
    {
      name: "isLiabilityAccepted",
      required: true,
      label: LABEL_LIABILITY,
      itemProps: {
        rules: [
          {
            message: LABEL_YES_REQUIRED,
            type: "enum",
            enum: [ true ],
          },
        ],
      },
      inputProps: {
        onChange,
      },
    },
    {
      name: "hasPurchasedExemptSecurities",
      required: true,
      label: LABEL_PURCHASED_EXEMPT_SECURITIES,
      itemProps: {
        extra: <Disclaimer text={TOOLTIP_PURCHASED_EXEMPT_SECURITIES} />,
      },
    },
  ];
};

export default computeLiabilityInputs;
